import React, { useState, useEffect } from "react";
import { CardHeader } from "app/popleads/components/ui-kit/Card";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import { POPUP_CONTENTS } from "../utils/constants";
import { useExtensionContext } from "../ExtensionContext";
import useTaskUpdate from "app/popleads/hooks/useTaskUpdate";
import useGetTaskLists from "app/popleads/hooks/useGetTaskLists";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import { TaskColorIcon } from "./common/styled";
import moment from "moment";
import styled from "styled-components";
import { colors } from "app/popleads/components/ui-kit/colors";
import EmptyStatus from "app/popleads/components/ui-kit/EmptyStatus";
import "react-datepicker/dist/react-datepicker.css";
import CustomDatePicker from "app/popleads/components/ui-kit/DateInput";
import { toast } from "react-toastify";
import useTaskComplete from "app/popleads/hooks/useTaskComplete";
import { useSelector } from "react-redux";
import FeatherIcon from "feather-icons-react";
import useTaskImportant from "app/popleads/hooks/useTaskImportant";
import { apiErrorText } from "app/popleads/networking/apiErrorText";

const StyledCollapseIcon = styled.div`
    cursor: pointer;
    color: ${colors.neutral[600]};
`;
const MoveDropdown = styled.div`
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: ${colors.white};
    border: 1px solid ${colors.neutral[200]};
    border-radius: 6px;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.15);
    padding: 8px 0;
    min-width: 120px;
    z-index: 9999;
    display: none;
    margin-bottom: 8px;

    &::after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        border-width: 8px 8px 0 8px;
        border-style: solid;
        border-color: ${colors.white} transparent transparent transparent;
    }

    &::before {
        content: "";
        position: absolute;
        bottom: -9px;
        left: 50%;
        transform: translateX(-50%);
        border-width: 9px 9px 0 9px;
        border-style: solid;
        border-color: ${colors.neutral[200]} transparent transparent transparent;
    }
`;

const MoveOption = styled.div`
    padding: 8px 16px;
    color: ${colors.neutral[800]};
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background: ${colors.neutral[50]};
    }
`;

const MoveButtonWrapper = styled.div`
    position: relative;

    &:hover ${MoveDropdown} {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    ${MoveDropdown} {
        display: ${(props) => (props.showDatePicker ? "none !important" : "none")};
    }
`;

const DatePickerWrapper = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-52%);
    z-index: 10;
    //background: ${colors.white};
    border-radius: 8px;
    //box-shadow: 0px 2px 8px rgba(73, 105, 140, 0.1);
`;

const TaskCarouselContainer = styled.div`
    position: relative;
    z-index: ${(props) => (props.showDatePicker ? "10000" : "1")};

    .task-header {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease-in-out;
        padding: 0;
    }

    &:hover {
        z-index: 100;
    }

    &:hover .task-header {
        max-height: 100px;
    }
    width: 100%;
`;

const TaskCarouselContent = styled.div`
    flex: 1;
`;

const CarouselNavButton = styled.button`
    background: transparent;
    border: none;
    border-radius: 4px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${colors.l3rnTech[900]};
    transition: all 0.2s ease;
    flex-shrink: 0;
    padding: 0;

    &:hover:not(:disabled) {
        background: ${colors.neutral[100]};
        color: ${colors.neutral[700]};
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }
`;

const HeaderButtonsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const TaskCompactView = styled.div`
    background: rgb(250, 250, 250);
    border: 1px solid ${colors.neutral[200]};
    border-radius: 8px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    overflow: visible;
    position: relative;

    &:hover {
        background: rgb(250, 250, 250);
        border-color: ${colors.l3rnTech[300]};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
`;

const TaskScheduledTime = styled.div`
    font-size: 11px;
    color: ${colors.neutral[500]};
    line-height: 1.3;
    margin-bottom: 4px;
`;

const TaskCompactHeader = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.neutral[800]};
    line-height: 1.4;
    word-break: break-word;

    & > p {
        margin: 0;
        display: inline;
    }
`;

const TaskHoverDetails = styled.div`
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out, padding 0.3s ease-in-out, opacity 0.3s ease-in-out;
    opacity: 0;
    padding: 0;

    ${TaskCompactView}:hover & {
        max-height: 300px;
        padding-top: 12px;
        opacity: 1;
        overflow: visible;
    }
`;

const TaskDetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    font-size: 13px;
    color: ${colors.neutral[600]};
`;

const TaskDetailLabel = styled.span`
    color: ${colors.neutral[500]};
`;

const TaskDetailValue = styled.span`
    color: ${colors.neutral[800]};
    display: flex;
    align-items: center;
    gap: 6px;
`;

const TaskHoverActions = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 4px;
    margin-top: 8px;
`;

const StyledTooltip = styled(Tooltip)`
    .tooltip-inner {
        font-size: 11px !important;
    }
`;

const ImportantBadge = styled.div`
    width: 18px;
    height: 18px;
    background-color: ${(props) => (props.important ? colors.danger[900] : colors.neutral[300])};
    border-radius: 50%;
    cursor: pointer;
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: bold;
    color: ${colors.white};
    transition: all 0.2s ease;
    border: 2px solid ${colors.white};
    z-index: 10;

    &:hover {
        background-color: ${(props) => (props.important ? colors.danger[600] : colors.neutral[400])};
        transform: scale(1.1);
    }
`;

const ExtensionTaskCard = ({ setTaskToDelete }) => {
    const {
        collapsedCards,
        setCollapsedCard,
        getTaskListLoading,
        taskRef,
        taskCarouselIndex,
        setState,
        ...ctx
    } = useExtensionContext();
    const { updateTask } = useTaskUpdate();
    const { refetch: fetchTaskLists } = useGetTaskLists();
    const { completeTask } = useTaskComplete();
    const [selectedTask, setSelectedTask] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const { toggleTaskImportant } = useTaskImportant();
    //const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
    const { user } = useSelector((state) => state.auth);

    const collapsed = collapsedCards.task;
    const handleCollapseChange = (val) => {
        setCollapsedCard("task", val);
    };

    const hasTaskPermission = (task) => {
        if (task.kullaniciId) {
            return task.kullaniciId && task.kullaniciId === user?.id;
        }
        return true;
    };

    const handleNoPermission = () => {
        toast.error("You do not have permission to edit this task");
    };

    const handleTaskDeleteClick = (task) => {
        if (!hasTaskPermission(task)) {
            handleNoPermission();
            return;
        }

        setState((prev) => ({
            ...prev,
            selectedModal: ctx.modals.DELETE_TASK,
            modalMessage:
                !!task.assignedById && user?.id !== task.assignedById
                    ? "This task has been assigned to you by other user. Are you sure you want to delete it?"
                    : "Are you sure you want to delete this task?",
        }));
        setTaskToDelete(task.id);
    };

    const handleTaskClick = (zaman, id, gorev) => {
        if (user?.name !== gorev.kayitci) {
            toast.error("You are not the owner of the task!");
            return;
        }

        setState((prev) => ({
            ...prev,
            selectedTab: "Tasks",
            selectedDate: new Date(zaman),
            selectedTasksId: id,
            popupContent: null,
            popupIsOpen: false,
        }));
    };

    const handleDateChange = (date) => {
        if (selectedTask && date) {
            const selectedDate = new Date(date);
            const currentDate = new Date(selectedTask.zaman);
            const timeDiff = selectedDate.getTime() - currentDate.getTime();
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

            handleMove(selectedTask.id, selectedTask.metin, selectedTask.zaman, selectedTask.profileId, daysDiff, true);
            setShowDatePicker(false);
            setSelectedTask(null);
        }
    };

    const handleOtherClick = (task) => {
        setSelectedTask(task);
        setShowDatePicker(true);
    };

    const moveOptions = [
        { label: "+7 days", value: 7 },
        { label: "+3 days", value: 3 },
        { label: "+1 day", value: 1 },
        { label: "Other", value: "other" },
    ];

    const handleMove = async (taskId, title, zaman, profileId, days, fromOtherDatePicker = false) => {
        const task = ctx.profile?.gorev?.find((g) => g.id === taskId);
        if (task && !hasTaskPermission(task)) {
            handleNoPermission();
            return;
        }

        try {
            const isOverdue = moment(zaman).isBefore(moment(), "day");
            const originalTime = moment(zaman);

            if (fromOtherDatePicker) {
                // Other: task date + daysDiff → exact picked date
                const newDate = moment(zaman)
                    .add(days, "days")
                    .hour(originalTime.hour())
                    .minute(originalTime.minute())
                    .second(originalTime.second())
                    .valueOf();
                await updateTask({
                    taskId,
                    taskText: title,
                    currentDate: newDate,
                    profileId,
                    daysToAdd: 0,
                    important: task.important,
                });
            } else if (isOverdue) {
                // +1/+3/+7 on overdue: today + days
                const today = moment().startOf("day");
                const newDate = today
                    .add(days, "days")
                    .hour(originalTime.hour())
                    .minute(originalTime.minute())
                    .second(originalTime.second())
                    .valueOf();
                await updateTask({
                    taskId,
                    taskText: title,
                    currentDate: newDate,
                    profileId,
                    daysToAdd: 0,
                    important: task.important,
                });
            } else {
                // Future: current date + days
                await updateTask({
                    taskId,
                    taskText: title,
                    currentDate: zaman,
                    profileId,
                    daysToAdd: days,
                    important: task.important,
                });
            }

            toast.success(`Task moved by ${days} day${Math.abs(days) !== 1 ? "s" : ""}`);
            fetchTaskLists();
            ctx.refetchTaskList();
        } catch (error) {}
    };

    const handleTaskComplete = async (taskId) => {
        try {
            const response = await completeTask(taskId);
            if (response?.stat === "ok") {
                //ctx.refetchProfile();
                ctx.refetchTaskList();
                fetchTaskLists();
                toast.success("Task completed");
                const messagePayload = {
                    message: "XPIDER_MESSAGE_NOTIFICATION",
                    task: {
                        id: taskId,
                        status: "completed",
                    },
                };
                window.parent.postMessage(messagePayload, "*");
            } else {
                toast.error(apiErrorText({ data: response }));
            }
        } catch (error) {
            toast.error(apiErrorText(error));
        }
    };

    const handleImportantToggle = async (taskId, currentImportant) => {
        const task = ctx.profile?.gorev?.find((g) => g.id === taskId);
        if (task && !hasTaskPermission(task)) {
            handleNoPermission();
            return;
        }

        const isEditingThisTask = ctx.editMode && ctx.selectedTask && ctx.selectedTask.id === taskId;

        if (isEditingThisTask) {
            toast.error("You can't change importance while editing this task.");
            return;
        }

        try {
            const response = await toggleTaskImportant(taskId, currentImportant);
            if (response?.data?.stat === "ok") {
                //ctx.refetchProfile();
                ctx.refetchTaskList();
                fetchTaskLists();
                toast.success(currentImportant ? "Task marked as normal" : "Task marked as important");
            } else {
                throw response;
            }
        } catch (error) {
            toast.error(apiErrorText(error));
        }
    };

    const tasks = ctx.profile?.gorev || [];
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const sortedTasks = [...tasks].sort((a, b) => {
        const dateA = new Date(a.zaman);
        const dateB = new Date(b.zaman);

        const dateAOnly = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
        const dateBOnly = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate());

        const diffA = Math.abs(dateAOnly - todayOnly);
        const diffB = Math.abs(dateBOnly - todayOnly);

        return diffA - diffB;
    });

    // Reset currentTaskIndex when tasks change
    useEffect(() => {
        if (taskCarouselIndex >= sortedTasks.length && sortedTasks.length > 0) {
            setState((prev) => ({ ...prev, taskCarouselIndex: sortedTasks.length - 1 }));
        } else if (sortedTasks.length === 0) {
            setState((prev) => ({ ...prev, taskCarouselIndex: 0 }));
        }
    }, [sortedTasks.length, taskCarouselIndex]);

    const handlePrevTask = (e) => {
        e.stopPropagation();
        if (taskCarouselIndex > 0) {
            setState((prev) => ({ ...prev, taskCarouselIndex: taskCarouselIndex - 1 }));
        }
    };

    const handleNextTask = (e) => {
        e.stopPropagation();
        if (taskCarouselIndex < sortedTasks.length - 1) {
            setState((prev) => ({ ...prev, taskCarouselIndex: taskCarouselIndex + 1 }));
        }
    };

    const handleTasksOpen = () => {
        if (ctx.popit) {
            toast.warning("You need to POP the profile first.");
        } else {
            setState((prev) => ({ ...prev, popupContent: POPUP_CONTENTS.PROFILE_TASKS, popupIsOpen: true }));
        }
    };

    const currentTask = sortedTasks[taskCarouselIndex];

    const renderCarouselTask = (gorev) => {
        if (!gorev) return null;

        const isEditingThisTask = ctx.editMode && ctx.selectedTask && ctx.selectedTask.id === gorev.id;
        const hasPermission = hasTaskPermission(gorev);

        const handleEditClick = (e) => {
            e.stopPropagation();
            if (!hasPermission) {
                handleNoPermission();
                return;
            }
            setState((prev) => ({
                ...prev,
                popupContent: POPUP_CONTENTS.NEW_TASK,
                popupIsOpen: true,
                taskPage: true,
                selectedTask: gorev,
                editMode: true,
            }));
        };

        const handleCompleteClick = (e) => {
            e.stopPropagation();
            if (!hasPermission) {
                handleNoPermission();
                return;
            }
            if (isEditingThisTask) {
                toast.error("You can't complete this task while editing.");
                return;
            }
            handleTaskComplete(gorev.id);
        };

        const handleDeleteClick = (e) => {
            e.stopPropagation();
            if (!hasPermission) {
                handleNoPermission();
                return;
            }
            if (isEditingThisTask) {
                toast.error("You can't delete this task while editing.");
                return;
            }
            handleTaskDeleteClick(gorev);
        };

        return (
            <TaskCompactView onClick={() => handleTaskClick(gorev.zaman, gorev.id, gorev)}>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <StyledTooltip id="tooltip-important">
                            {gorev.important === 1 ? "Click to mark as normal" : "Click to mark as important"}
                        </StyledTooltip>
                    }
                >
                    <ImportantBadge
                        important={gorev.important === 1}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleImportantToggle(gorev.id, gorev.important);
                        }}
                    >
                        !
                    </ImportantBadge>
                </OverlayTrigger>
                {(gorev.zaman || gorev.assignedByName) && (
                    <TaskScheduledTime>
                        {gorev.zaman && moment(gorev.zaman).format("DD MMM YYYY HH:mm")}
                        {gorev.zaman && gorev.assignedByName && " | "}
                        {gorev.assignedByName && `${gorev.assignedByName}`}
                    </TaskScheduledTime>
                )}
                <TaskCompactHeader
                    dangerouslySetInnerHTML={{
                        __html: `${gorev.metin || "No description"} | <span style="color: #2b2b75;">${
                            gorev.profil?.ad && gorev.profil?.soyad
                                ? `${gorev.profil.ad} ${gorev.profil.soyad}`
                                : gorev.profil?.ad || "No customer"
                        }</span>`,
                    }}
                />

                <TaskHoverDetails>
                    <TaskDetailRow>
                        <TaskDetailLabel>Type:</TaskDetailLabel>
                        <TaskDetailValue>{gorev.type || "N/A"}</TaskDetailValue>
                    </TaskDetailRow>
                    <TaskDetailRow>
                        <TaskDetailLabel>Created Date:</TaskDetailLabel>
                        <TaskDetailValue>{moment(gorev.createdTime).format("DD MMM YYYY HH:mm")}</TaskDetailValue>
                    </TaskDetailRow>
                    <TaskDetailRow>
                        <TaskDetailLabel>Task Color:</TaskDetailLabel>
                        <TaskDetailValue>
                            <TaskColorIcon color={gorev.taskColour || colors.neutral[400]} />
                        </TaskDetailValue>
                    </TaskDetailRow>

                    <TaskHoverActions>
                        <ButtonWithText
                            size="xsmall"
                            variant="success"
                            buttonStyle="borderless"
                            onClick={handleCompleteClick}
                            style={{ margin: "0px", padding: "4px 8px" }}
                        >
                            Complete
                        </ButtonWithText>
                        <MoveButtonWrapper showDatePicker={showDatePicker && selectedTask?.id === gorev.id}>
                            <ButtonWithText
                                variant="passive"
                                buttonStyle="borderless"
                                size="xsmall"
                                style={{ margin: "0px", padding: "4px 8px" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!hasPermission) {
                                        handleNoPermission();
                                        return;
                                    }
                                    if (isEditingThisTask) {
                                        toast.error("You can't move this task while editing.");
                                        return;
                                    }
                                }}
                            >
                                Move
                            </ButtonWithText>
                            {!isEditingThisTask && !(showDatePicker && selectedTask?.id === gorev.id) && (
                                <MoveDropdown>
                                    {moveOptions.map((option) => (
                                        <MoveOption
                                            key={option.value}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                option.value === "other"
                                                    ? handleOtherClick(gorev)
                                                    : handleMove(
                                                          gorev.id,
                                                          gorev.metin,
                                                          gorev.zaman,
                                                          gorev.profileId,
                                                          option.value
                                                      );
                                            }}
                                        >
                                            {option.label}
                                        </MoveOption>
                                    ))}
                                </MoveDropdown>
                            )}
                            {showDatePicker && selectedTask?.id === gorev.id && (
                                <DatePickerWrapper
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <CustomDatePicker
                                        componentVariant="l3rnTech"
                                        variant="move-dropdown"
                                        onChange={handleDateChange}
                                        onClickOutside={() => {
                                            setShowDatePicker(false);
                                            setSelectedTask(null);
                                        }}
                                    />
                                </DatePickerWrapper>
                            )}
                        </MoveButtonWrapper>
                        <ButtonWithText
                            size="xsmall"
                            variant="danger"
                            buttonStyle="borderless"
                            onClick={handleDeleteClick}
                            style={{ margin: "0px", padding: "4px 8px" }}
                        >
                            Delete
                        </ButtonWithText>
                        <ButtonWithText
                            size="xsmall"
                            variant="l3rnTech"
                            buttonStyle="borderless"
                            onClick={handleEditClick}
                            style={{ margin: "0px", padding: "4px 8px" }}
                        >
                            Edit
                        </ButtonWithText>
                    </TaskHoverActions>
                </TaskHoverDetails>
            </TaskCompactView>
        );
    };

    return (
        <div ref={taskRef} style={{ padding: "12px 0px", borderBottom: "1px solid rgb(225, 225, 225)" }}>
            <span className="d-flex">
                <div className="mr-2" onClick={() => handleCollapseChange(!collapsed)}>
                    {collapsed ? (
                        <StyledCollapseIcon>
                            <FeatherIcon icon="chevron-down" size={17} />
                        </StyledCollapseIcon>
                    ) : (
                        <StyledCollapseIcon>
                            <FeatherIcon icon="chevron-up" size={17} />
                        </StyledCollapseIcon>
                    )}
                </div>
                <CardHeader
                    cardTitle={`Tasks (${sortedTasks.length})`}
                    content="tasks"
                    cardTitleOnClick={handleTasksOpen}
                    button={
                        <HeaderButtonsWrapper>
                            {sortedTasks.length > 1 && (
                                <>
                                    <CarouselNavButton
                                        onClick={handlePrevTask}
                                        disabled={taskCarouselIndex === 0}
                                        aria-label="Previous task"
                                    >
                                        <FeatherIcon icon="chevron-left" size={24} />
                                    </CarouselNavButton>
                                    <CarouselNavButton
                                        onClick={handleNextTask}
                                        disabled={taskCarouselIndex === sortedTasks.length - 1}
                                        aria-label="Next task"
                                    >
                                        <FeatherIcon icon="chevron-right" size={24} />
                                    </CarouselNavButton>
                                </>
                            )}
                            <ButtonWithText
                                label="Add task"
                                icon="add"
                                iconType="material"
                                variant="l3rnTech"
                                buttonStyle="default"
                                size="circle"
                                onClick={() => {
                                    if (ctx.popit) {
                                        toast.warning("You need to POP the profile first.");
                                    } else {
                                        setState((prev) => ({
                                            ...prev,
                                            popupContent: POPUP_CONTENTS.NEW_TASK,
                                            popupIsOpen: true,
                                            taskPage: false,
                                            editMode: false,
                                        }));
                                    }
                                }}
                            />
                        </HeaderButtonsWrapper>
                    }
                />
            </span>
            {getTaskListLoading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        padding: "12px",
                    }}
                >
                    <Spinner size={32} thickness={4} variant="l3rnTech" />
                </div>
            ) : (
                !collapsed && (
                    <TaskCarouselContainer style={{ marginTop: "10px" }}>
                        {sortedTasks.length > 0 ? (
                            <TaskCarouselContent>{renderCarouselTask(currentTask)}</TaskCarouselContent>
                        ) : (
                            <EmptyStatus description="Click the + to create a new task." />
                        )}
                    </TaskCarouselContainer>
                )
            )}
        </div>
    );
};

export default ExtensionTaskCard;
